import React, { useState, useEffect } from 'react';
import { X, Loader } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { apiCall } from '../../services/apiUtil';

const REACTIONS = [
  { type: 'like',    emoji: '👍', label: 'Like' },
  { type: 'love',    emoji: '❤️', label: 'Love' },
  { type: 'haha',    emoji: '😂', label: 'Haha' },
  { type: 'sad',     emoji: '😢', label: 'Sad' },
  { type: 'dislike', emoji: '👎', label: 'Dislike' },
];

/**
 * ReactorsModal — shows who reacted to a post or comment, with tabs per reaction type.
 *
 * Props:
 * - endpoint  — API path, e.g. '/posts/:id/reactors' or '/posts/:id/comments/:cid/reactors'
 * - counts    — reactionCounts object { like: 3, love: 2, ... } — used to build tabs
 * - initialTab — reaction type to show first (optional, defaults to most-used)
 * - onClose   — called when modal is dismissed
 */
const ReactorsModal = ({ endpoint, counts = {}, initialTab, onClose }) => {
  const { theme } = useTheme();

  const activeTabs = REACTIONS.filter(r => (counts[r.type] || 0) > 0);
  const total = activeTabs.reduce((sum, r) => sum + (counts[r.type] || 0), 0);

  const defaultTab = initialTab && (counts[initialTab] || 0) > 0
    ? initialTab
    : activeTabs[0]?.type || 'like';

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [reactors, setReactors] = useState(null); // { like: [...], love: [...], ... }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiCall(endpoint)
      .then(result => {
        if (!cancelled) {
          setReactors(result.success ? result.data : {});
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [endpoint]);

  const users = reactors?.[activeTab] || [];

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      <div className={`${theme.card} rounded-xl shadow-2xl w-full max-w-sm`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${theme.border}`}>
          <h3 className={`font-semibold ${theme.text}`}>Reactions</h3>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-full ${theme.cardHover} transition-colors`}
          >
            <X className={`w-4 h-4 ${theme.textMuted}`} />
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex border-b ${theme.border} overflow-x-auto scrollbar-hide`}>
          {/* All tab */}
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
              activeTab === 'all'
                ? `border-blue-500 text-blue-500`
                : `border-transparent ${theme.textMuted} hover:${theme.text}`
            }`}
          >
            All {total}
          </button>
          {activeTabs.map(r => (
            <button
              key={r.type}
              onClick={() => setActiveTab(r.type)}
              className={`flex items-center gap-x-1 px-4 py-2.5 text-sm whitespace-nowrap transition-colors border-b-2 ${
                activeTab === r.type
                  ? `border-blue-500`
                  : `border-transparent ${theme.textMuted} hover:${theme.text}`
              }`}
            >
              <span>{r.emoji}</span>
              <span>{counts[r.type]}</span>
            </button>
          ))}
        </div>

        {/* User list */}
        <div className="max-h-72 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : error ? (
            <p className={`text-center py-8 text-sm ${theme.textMuted}`}>Failed to load reactions.</p>
          ) : (() => {
            const list = activeTab === 'all'
              ? REACTIONS.flatMap(r => (reactors?.[r.type] || []).map(u => ({ ...u, reactionType: r.type })))
              : (reactors?.[activeTab] || []).map(u => ({ ...u, reactionType: activeTab }));

            return list.length === 0 ? (
              <p className={`text-center py-8 text-sm ${theme.textMuted}`}>No reactions yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {list.map((u, i) => {
                  const reaction = REACTIONS.find(r => r.type === u.reactionType);
                  return (
                    <li key={`${u.id}-${i}`} className={`flex items-center justify-between px-4 py-3 ${theme.cardHover} transition-colors`}>
                      <div className="flex items-center gap-x-3">
                        {u.avatar ? (
                          <img src={u.avatar} alt={u.username} className="w-9 h-9 rounded-full object-cover" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-white">
                              {u.username?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                        <span className={`text-sm font-medium ${theme.text}`}>{u.username}</span>
                      </div>
                      <span className="text-lg leading-none">{reaction?.emoji}</span>
                    </li>
                  );
                })}
              </ul>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default ReactorsModal;
